import React from 'react';
import { Asset } from 'contentful';

import { IStaticFullWidthHeroFields } from '@/types/contentful';

import { getImageData, getImageDataWithHttps } from '../../utils/contentful';
import { useMultipleVideoCache } from '../../utils/videoCache';

import { Box } from '../Box';
import { Column, Layout } from '../Layout';
import CtaWidget from '../CtaWidget';

import {
  DesktopImage,
  RelativeBox,
  CustomBlockContainer,
  MobileImage,
  AdditionalContentWrapper,
  ParagraphWithLineBreaks,
  StyledIcon
} from './styles';
import PrivacyPolicyDisclaimer from 'src/components/PrivacyPolicyDisclaimer';
import { Flexbox } from '../Flexbox';
import GlImage from '../Image';
import Image from 'next/image';
import AdditionalContent from '../AdditionalContent';
import { RenderNode } from '@contentful/rich-text-react-renderer';
import { BLOCKS } from '@contentful/rich-text-types';
import RichText from '../RichText';

import Lottie from 'lottie-react';
import { animated, useSpring, easings } from 'react-spring';
import { usePageDataContext } from '~/context/PageData';
import cx from 'classnames';
import { SmartText } from '../SmartText';

function useAnimatedFadeIn(key: number) {
  return useSpring({
    to: { opacity: 1, transform: 'translateY(0)' },
    from: { opacity: 0, transform: 'translateY(50px)' },
    delay: 300 + key * 500,
    config: {
      duration: 1500,
      easing: easings.easeOutExpo
    }
  });
}

export default function AnimatedFullWidthHero({
  headingText,
  subheadingText,
  desktopBackgroundImage,
  fullwidthDesktopImage,
  animatedImage,
  mobileImage,
  additionalText,
  privacyPolicyDisclaimer,
  enableBlackText,
  ctaWidget,
  additionalContent,
  returningUserHeadingText,
  customTextColor,
  backgroundColor,
  eyebrowImage,
  assetFirstOnMobile,
  enablePaddingOnMobileImage
}: IStaticFullWidthHeroFields) {
  const { isReturning } = usePageDataContext();
  const visibility =
    fullwidthDesktopImage || mobileImage ? 'visible' : 'hidden';

  // Check if assets are videos (based on content type)
  const isDesktopVideo =
    fullwidthDesktopImage?.fields?.file?.contentType?.includes('mp4') ||
    fullwidthDesktopImage?.fields?.file?.contentType?.includes('video');
  const isMobileVideo =
    mobileImage?.fields?.file?.contentType?.includes('mp4') ||
    mobileImage?.fields?.file?.contentType?.includes('video');

  // Cache videos using the reusable hook
  const [cachedDesktopVideoUrl, cachedMobileVideoUrl] = useMultipleVideoCache([
    isDesktopVideo ? fullwidthDesktopImage?.fields?.file?.url : undefined,
    isMobileVideo ? mobileImage?.fields?.file?.url : undefined
  ]);

  // Determines the whether text color should be overridden
  const defaultTextColor = enableBlackText ? 'black' : 'white';
  const textColorOverride = customTextColor
    ? customTextColor
    : defaultTextColor;

  //Determines the hero media to display
  const determineHeroMedia = (
    assetFirstOnMobileEnabled: boolean,
    bottomMobileAsset: boolean
  ) => {
    if (animatedImage) {
      return (
        <Lottie
          data-testid="animated-image"
          className={cx('mobile:h-[408px] tablet:h-[460px] lg:h-[560px]', {
            hidden: assetFirstOnMobileEnabled && bottomMobileAsset
          })}
          animationData={animatedImage}
          loop={true}
        />
      );
    }

    if (isDesktopVideo && isMobileVideo) {
      return (
        <>
          <div className="hidden tablet:block tablet:pl-4 desktop:pl-0">
            <video
              data-testid="desktop-video"
              className="h-full max-h-[584px] w-full object-contain object-center"
              autoPlay
              muted
              playsInline
              src={cachedDesktopVideoUrl}
            />
          </div>
          <div className="block tablet:hidden">
            <video
              data-testid="mobile-video"
              className={cx('h-full w-full object-cover object-center', {
                hidden: assetFirstOnMobileEnabled && bottomMobileAsset,
                'p-6': !!enablePaddingOnMobileImage
              })}
              autoPlay
              muted
              playsInline
              src={cachedMobileVideoUrl}
            />
          </div>
        </>
      );
    } else {
      // If there is no video, render the GL images
      return (
        <>
          <div className="hidden tablet:block">
            <DesktopImage
              {...getImageData(fullwidthDesktopImage as Asset)}
              visibility={visibility}
              loading="eager"
              heroImage={true}
              objectFit="contain"
            />
          </div>
          <div
            data-testid="mobile-image"
            className={cx('block pt-3 tablet:hidden', {
              hidden: assetFirstOnMobileEnabled && bottomMobileAsset,
              'p-6': !!enablePaddingOnMobileImage
            })}
          >
            <MobileImage
              {...getImageData(mobileImage as Asset)}
              heroImage={true}
              loading="eager"
              data-testid="fullwidth-hero-mobile-background"
              objectFit="contain"
            />
          </div>
        </>
      );
    }
  };

  const RICH_TEXT_OVERRIDE: RenderNode = {
    [BLOCKS.PARAGRAPH]: (_node, children) => {
      return (
        <ParagraphWithLineBreaks
          as="p"
          fontSize={{
            0: '2xs',
            tablet: 's'
          }}
          fontWeight="400"
          colour={textColorOverride}
          textAlign="left"
        >
          {children}
        </ParagraphWithLineBreaks>
      );
    },
    [BLOCKS.EMBEDDED_ASSET]: (node) => (
      <StyledIcon {...getImageData(node.data.target)} />
    )
  };

  return (
    <Box
      data-blockid="static-fullwidth-hero"
      data-e2e="hero"
      data-testid="static-fullwidth-hero"
      className="mb-16"
    >
      <div
        className={'md:max-h-[624px] relative grid w-full pb-8 lg:h-full'}
        style={{
          backgroundColor: backgroundColor ? backgroundColor : 'transparent'
        }}
      >
        {desktopBackgroundImage && (
          <div style={{ gridArea: '1/1' }}>
            <GlImage
              {...getImageData(desktopBackgroundImage as Asset)}
              loading="eager"
              heroImage={false}
              layout="fill"
              objectFit="fill"
              width={undefined}
              height={undefined}
            />
          </div>
        )}
        <div
          style={{
            gridArea: '1/1',
            position: 'relative',
            placeItems: 'center',
            display: 'grid'
          }}
        >
          {assetFirstOnMobile && (
            <animated.div className="relative left-0 top-0 block w-full tablet:hidden">
              {determineHeroMedia(!!assetFirstOnMobile, false)}
            </animated.div>
          )}
          <CustomBlockContainer
            marginTop={{
              0: 'ml',
              tablet: 'blockMargin'
            }}
            paddingBottom={
              !additionalContent
                ? {}
                : {
                    0: 'blockMarginY',
                    tablet: 'l'
                  }
            }
            revealOnScroll={false}
          >
            <Layout
              gutter={{ tablet: '2xl', desktop: 'collapsibleVert' }}
              marginTop="s"
            >
              <Column
                className="z-[1]"
                size={{ 0: 11, tablet: 6 }}
                flexDirection="column"
                justifyContent="center"
              >
                <Flexbox
                  flexDirection={{ 0: 'column' }}
                  margin={[0, 'auto']}
                  justifyContent={{ 0: 'center', tablet: 'left' }}
                  alignItems={{ 0: 'center', tablet: 'normal' }}
                >
                  {eyebrowImage && (
                    <div className="z-1 mb-7 h-6 w-36">
                      <Image
                        {...getImageDataWithHttps(eyebrowImage as Asset)}
                        alt={'Animated Fullwidth Logo'}
                        loading="lazy"
                        objectFit="contain"
                      />
                    </div>
                  )}
                  <SmartText>
                    <animated.div style={useAnimatedFadeIn(0)}>
                      <h1
                        data-testid="fullwidth-hero-headline"
                        className={`min:leading-[38.4px] mx-auto mb-4 mt-0 text-center text-[32px] font-normal tablet:mx-0 tablet:text-left desktop:text-[52px] desktop:leading-[55.2px]`}
                        style={{ color: textColorOverride }}
                      >
                        {isReturning
                          ? returningUserHeadingText ?? headingText
                          : headingText}
                      </h1>
                    </animated.div>
                    <animated.div style={useAnimatedFadeIn(1)}>
                      <p
                        data-testid="fullwidth-hero-subhead"
                        className={`mx-auto mb-4 mt-0 text-center text-xl tablet:mx-0 tablet:text-left tablet:text-2xl`}
                        style={{ color: textColorOverride }}
                      >
                        {subheadingText}
                      </p>
                    </animated.div>
                  </SmartText>
                  <animated.div style={useAnimatedFadeIn(2)}>
                    {ctaWidget && (
                      <div className="mb-2 flex justify-start tablet:max-w-[350px] desktop:mb-0 desktop:justify-normal">
                        <CtaWidget
                          {...ctaWidget?.fields}
                          sysId={ctaWidget?.sys?.contentType?.sys?.id}
                          id="fullwidth-hero-cta"
                          module="Hero"
                          height={56}
                          width={'100%'}
                          isMobile={false}
                          responsive
                        />
                      </div>
                    )}
                    {additionalText && (
                      <Column size={10} margin={0}>
                        <Flexbox
                          flexDirection="row"
                          justifyContent="space-between"
                          margin={0}
                          alignItems="left"
                        >
                          <AdditionalContentWrapper data-e2e="additional-content-container">
                            <RichText
                              data={additionalText}
                              overrideOptions={RICH_TEXT_OVERRIDE}
                            />
                          </AdditionalContentWrapper>
                        </Flexbox>
                      </Column>
                    )}
                    {privacyPolicyDisclaimer && (
                      <div className="mt-2">
                        <PrivacyPolicyDisclaimer
                          privacyPolicyDisclaimer={privacyPolicyDisclaimer}
                          displayPrivacyPolicyIcon={false}
                          color={defaultTextColor}
                        />
                      </div>
                    )}
                  </animated.div>
                </Flexbox>
              </Column>
              <Column
                size={{ 0: 12, tablet: 7, desktop: 6 }}
                marginTop={{ 0: 'sm', tablet: 'auto' }}
                paddingLeft={{ 0: 'sm', tablet: 'auto' }}
                paddingRight={{ 0: 'sm', tablet: 'auto' }}
              >
                <RelativeBox flex="1">
                  <animated.div style={useAnimatedFadeIn(3)}>
                    {determineHeroMedia(!!assetFirstOnMobile, true)}
                  </animated.div>
                </RelativeBox>
              </Column>
            </Layout>
          </CustomBlockContainer>
        </div>
      </div>
      <animated.div style={useAnimatedFadeIn(4)}>
        <AdditionalContent additionalContent={additionalContent} />
      </animated.div>
    </Box>
  );
}
